import React, { useState } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { Button, Grid, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import ImageUploadIconButton from '../common/image_upload_icon_button';
import {
  uploadUserImageVariants,
  VARIANT_SIZE_DESCRIPTOR_ORIGINAL,
} from '../../services/user_generated_content';
import {
  useGetSpaceQuery,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { applySmorgStudioSpaceWhiteLabelRules } from '../../services/spaces';
import { ActivityObjectType } from '../../API';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const FAVICON_SIZE_DESCRIPTORS = ['32x32', '192x192'];

const SpaceFaviconEditForm = () => {
  const { data: space } = useGetSpaceQuery();

  const [updateSpace] = useUpdateSpaceMutation();

  const navigate = useNavigate();

  const [whiteLabelElements, setWhiteLabelElements] = useState(
    space?.whiteLabelElements || {},
  );

  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const updatedSpace = {
        ...space,
        whiteLabelElements,
      };
      await updateSpace(updatedSpace);
      applySmorgStudioSpaceWhiteLabelRules(updatedSpace.whiteLabelElements);
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.SPACE,
        lastActivityObjectID: null,
      });
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Changes saved',
        }),
      );
      setIsDirty(false);
    } catch (err) {
      console.log({ err });
    } finally {
      setIsLoading(false);
    }
  };

  const onNewImageChosen = async (fileList) => {
    const faviconImageVariants = await uploadUserImageVariants(
      space.id,
      fileList,
      FAVICON_SIZE_DESCRIPTORS,
    );
    setWhiteLabelElements((oldWhiteLabelElements) => ({
      ...oldWhiteLabelElements,
      faviconImages: faviconImageVariants,
    }));
    afterChange();
  };

  const afterChange = () => {
    setIsDirty(true);
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const originalImageUrl = (whiteLabelElements?.faviconImages || []).find(
    (imgVariant) =>
      imgVariant.sizeDescriptor === VARIANT_SIZE_DESCRIPTOR_ORIGINAL,
  )?.imageUrl;

  useBlocker(() => {
    if (isDirty) {
      // eslint-disable-next-line no-alert
      return !window.confirm(
        'You have unsaved changes. Are you sure you want to leave this page?',
      );
    }
    return false; // Allow navigation
  });

  return (
    <div className="userProfile">
      <div className="banner">
        <IconButton
          aria-label="close"
          onClick={onGoBack}
          size="small"
          style={{ float: 'right', marginTop: -7 }}>
          <CloseIcon />
        </IconButton>
        Edit your favicon
      </div>
      <Grid container columnSpacing={1} className="userProfileForm">
        <Grid item xs={9}>
          <Grid container>
            <Grid item xs={12}>
              <h5>Customise your favicon</h5>
            </Grid>
            <Grid item xs={12} style={{ padding: '16px 0' }}>
              We recommend an image resolution of 192 by 192 pixels, and we
              support png and jpg.
            </Grid>
            {originalImageUrl && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    className="smorgStudioFaviconImage"
                    style={{
                      backgroundImage: `url("${originalImageUrl}")`,
                    }}>
                    &nbsp;
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} style={{ padding: '16px 0' }}>
              <ImageUploadIconButton onChange={onNewImageChosen} />
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ margin: '15px 0', padding: '6px' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty || isLoading}>
              Save
            </Button>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 20, height: 250 }}>
            {/* Spacer */}
            &nbsp;
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default SpaceFaviconEditForm;
